/**
 * WhatsNewLink — compact "What's New" button intended for the profile
 * slide-out drawer (above Sign Out). Shows a pulsing dot when the
 * latest released version is unseen for this user.
 *
 * **Source of truth**: `GET /api/changelog` — which parses
 * `/app/CHANGELOG.md` server-side. The static `data/changelog.js` is
 * kept only as an offline-mode fallback in case the API is unreachable.
 * (User mandate 2026-06-12 "the WowCrew slide-out and Reports page
 * should both pull from /app/CHANGELOG.md — neither hardcoded".)
 *
 * "Seen" state tracked per-crew in localStorage so each user gets
 * their own unseen dot. Marking-seen happens on modal open.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Sparkle, X, ArrowRight } from "@phosphor-icons/react";
import { LATEST as STATIC_LATEST, LATEST_PUBLIC_ITEMS as STATIC_FALLBACK } from "../data/changelog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEEN_KEY = "wow_changelog_seen_version";

function storageKey(crewId) {
  return crewId ? `${SEEN_KEY}::${crewId}` : SEEN_KEY;
}

// Parse the "What changed" block from a markdown entry into a flat
// list of bullet titles. The CHANGELOG.md format uses numbered top-
// level points ("1. **Title**: detail") and nested dashes; we surface
// just the top-level titles here so the slide-out doesn't drown in
// detail. Falls back to the raw first line if no structured items
// are found.
function parseItems(what) {
  if (!what) return [];
  const lines = String(what).split("\n");
  const items = [];
  for (const raw of lines) {
    // Numbered top-level: "1. **Title**: detail" or "1. Title"
    const num = /^\s*\d+\.\s+(?:\*\*([^*]+?)\*\*:?\s*)?(.*)$/.exec(raw);
    if (num) {
      const title = (num[1] || num[2] || "").trim().replace(/[:.]\s*$/, "");
      if (title) items.push({ type: "feature", title });
      continue;
    }
    // Bold-leading paragraphs without a number: "**Title**: ..."
    const bold = /^\s*\*\*([^*]+?)\*\*:?\s*(.*)$/.exec(raw);
    if (bold && items.length === 0) {
      const title = (bold[1] || "").trim();
      if (title) items.push({ type: "feature", title });
    }
  }
  if (items.length === 0) {
    // Last-ditch — show the first non-empty line.
    const first = lines.find((l) => l.trim());
    if (first) items.push({ type: "feature", title: first.trim().replace(/^[\-\*]\s*/, "").slice(0, 140) });
  }
  return items.slice(0, 8); // hard cap so the modal doesn't get huge
}

function typeBadge(type) {
  switch (type) {
    case "feature": return { label: "NEW", color: "#2563EB", bg: "#DBEAFE" };
    case "fix":     return { label: "FIX", color: "#16A34A", bg: "#DCFCE7" };
    case "polish":  return { label: "POLISH", color: "#9333EA", bg: "#EDE9FE" };
    case "security":return { label: "SECURITY", color: "#DC2626", bg: "#FEE2E2" };
    default:        return { label: "UPDATE", color: "#475569", bg: "#E2E8F0" };
  }
}

export default function WhatsNewLink({ crewId, onOpenFull }) {
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState(true);
  const [latest, setLatest] = useState(null); // { version, date, title, items }

  // Fetch the live changelog. The endpoint reads /app/CHANGELOG.md so
  // every code change picks up automatically — no parallel data file
  // to update. Static `STATIC_LATEST` is the offline fallback.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/changelog`)
      .then((r) => {
        if (cancelled) return;
        const e = (r.data?.entries || [])[0];
        if (e) {
          setLatest({
            version: e.version,
            date: e.date,
            title: e.title,
            items: parseItems(e.what),
          });
        }
      })
      .catch(() => {
        if (cancelled) return;
        setLatest({
          version: STATIC_LATEST.version,
          date: STATIC_LATEST.date,
          title: STATIC_LATEST.headline,
          items: STATIC_FALLBACK,
        });
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!latest) return;
    try {
      const last = localStorage.getItem(storageKey(crewId));
      setSeen(last === latest.version);
    } catch { /* localStorage blocked — assume seen */ }
  }, [crewId, latest]);

  if (!latest) return null;

  const openModal = () => {
    setOpen(true);
    try {
      localStorage.setItem(storageKey(crewId), latest.version);
      setSeen(true);
    } catch { /* blocked, fine */ }
  };

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl hover:bg-slate-100 active:bg-slate-200 transition-colors text-left"
        data-testid="whats-new-link"
      >
        <span className="relative inline-flex items-center justify-center w-7 h-7 rounded-lg bg-amber-100">
          <Sparkle weight="fill" className="w-4 h-4 text-amber-600" />
          {!seen && (
            <span
              className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center"
              data-testid="whats-new-unseen-dot"
            >
              <span className="absolute inset-0 rounded-full bg-rose-500 opacity-60 animate-ping" />
              <span className="relative w-2 h-2 rounded-full bg-rose-500 ring-2 ring-white" />
            </span>
          )}
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-xs font-black uppercase tracking-wider text-slate-900">What&apos;s New</span>
          <span className="block text-[10px] text-slate-500 truncate">v{latest.version} &middot; {latest.title}</span>
        </span>
        <ArrowRight weight="bold" className="w-3.5 h-3.5 text-slate-400" />
      </button>

      {open && (
        <WhatsNewModal
          latest={latest}
          onClose={() => setOpen(false)}
          onOpenFull={onOpenFull}
        />
      )}
    </>
  );
}

function WhatsNewModal({ latest, onClose, onOpenFull }) {
  return (
    <div
      className="fixed inset-0 z-[170] bg-black/55 backdrop-blur-sm flex items-center justify-center p-4 animate-[fadeIn_140ms_ease-out]"
      onClick={onClose}
      data-testid="whats-new-modal"
    >
      <div
        className="bg-white rounded-3xl max-w-sm w-full shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 pt-5 pb-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] font-black uppercase tracking-[0.18em] text-blue-600" data-testid="whats-new-version">
              v{latest.version} &middot; {latest.date}
            </p>
            <h2
              className="text-xl font-black tracking-tight text-slate-900 mt-0.5"
              style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}
              data-testid="whats-new-title"
            >
              {latest.title}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center flex-shrink-0"
            aria-label="Close"
            data-testid="whats-new-close"
          >
            <X weight="bold" className="w-4 h-4 text-slate-700" />
          </button>
        </div>

        <ul className="px-5 pb-4 space-y-2 max-h-[60vh] overflow-y-auto">
          {latest.items.map((it, idx) => {
            const b = typeBadge(it.type);
            return (
              <li key={idx} className="flex items-start gap-2.5">
                <span
                  className="inline-flex items-center justify-center px-1.5 h-5 rounded text-[9px] font-black tracking-wider flex-shrink-0 mt-0.5"
                  style={{ color: b.color, background: b.bg }}
                >
                  {b.label}
                </span>
                <p className="text-sm font-bold text-slate-900 leading-snug min-w-0">{it.title}</p>
              </li>
            );
          })}
        </ul>

        <div className="border-t border-slate-100 px-5 py-3 flex items-center justify-between gap-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
            Future updates land here as soon as they ship.
          </p>
          {onOpenFull && (
            <button
              type="button"
              onClick={() => { onClose(); onOpenFull(); }}
              className="text-[10px] font-bold uppercase tracking-wider text-blue-700 hover:text-blue-900"
              data-testid="whats-new-see-full"
            >
              Full log
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
